import PropTypes from 'prop-types';
import React from 'react';
import Flexbox from 'flexbox-react';
import { cx, css } from 'emotion';
import AbstractFilteredList from 'components/core/AbstractFilteredList';
import ListItem from 'components/core/ListItem';
import RollupError from 'components/core/RollupError';
import ProcessUtils from 'utils/ProcessUtils';
import Constants from 'utils/Constants';
import { Tooltip } from 'antd';

export default class RetrievedVariablesList extends AbstractFilteredList {

    static propTypes = {
        getBackgroundColor: PropTypes.func,
        process: PropTypes.object,
        hideBadges: PropTypes.bool, // Some times we don't want to show the badges, depending upon what form we are on
        showDataSources: PropTypes.bool,
        reverse: PropTypes.bool, // reverse alternating background color so white is first
        hideInputDatasetName: PropTypes.bool,
        showUnits: PropTypes.bool // display datastream units -> imported as units
    };

    static defaultProps = Object.assign({
        hideBadges: false,
        reverse: false,
        hideInputDatasetName: false,
        showUnits: false
    }, AbstractFilteredList.defaultProps);

    getItemClassName() {
        return 'entity retrieved-variable';
    }

    getListClassName() {
        return 'entity-list retrieved-variables';
    }

    /**
     * Override to add the "show data sources" button
     * @param filteredLength
     * @param length
     * @returns {*}
     */
    renderFilterSection(filteredLength, length) {

        const filter = this.getFilter();
        // Only show the filter box if we actually have data in the table
        if (this.props.filterable) {
            return (
                <Flexbox flexDirection="column" className="filter-wrapper">
                    <input
                        ref="filterInput"
                        className={this.getInputClassName()}
                        type="text" placeholder={this.getPlaceholder()}
                        onChange={this.onFilterChanged}
                        value={filter}
                        flex={1}
                    />
                    <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' }}>
                        <p className="found">Showing {filteredLength} of {length}: </p>
                    </div>
                </Flexbox>
            );
        } else {
            return '';
        }
    }

    render() {
        // I am setting these local variables because they are always reset when rendering, so it won't get
        // clobbered when switching tabs
        this.currentDataset = null;
        this.color = this.props.reverse ? AbstractFilteredList.COLOR_LIGHT_GREY : AbstractFilteredList.COLOR_WHITE;
        return super.render();
    }

    renderItem(name, value, selected) {
        const dataset = value.input_dataset;
        const dimensions = ProcessUtils.getRetrievedVariableDimensions(this.props.process, name);
        let datasetLabel = <div/>;

        let color = this.props.reverse ? AbstractFilteredList.COLOR_LIGHT_GREY : AbstractFilteredList.COLOR_WHITE;
        if (this.props.showDataSources) {
            color = this.color;
            if (dataset !== this.currentDataset) {

                this.currentDataset = dataset;
                if (color === AbstractFilteredList.COLOR_WHITE) {
                    color = AbstractFilteredList.COLOR_LIGHT_GREY;
                    this.color = color;
                } else {
                    color = AbstractFilteredList.COLOR_WHITE;
                    this.color = color;
                }

                const labelStyle = {
                    width: '100%',
                    fontWeight: '900',
                    backgroundColor: color,
                    paddingLeft: '3px',
                    color: '#337ab7',
                    paddingTop: '3px'
                };

                const datastreamsList = ProcessUtils.getInputDatasetDatastreamsLists(this.props.process, dataset);
                let datastreamsListComponent = datastreamsList.visibleList;
                if (datastreamsList.tooltip) {
                    datastreamsListComponent = <Tooltip placement={'top'} title={datastreamsList.fullList}>{datastreamsList.visibleList}</Tooltip>
                }

                let label = <div style={labelStyle}>{dataset}<span style={{ fontWeight: 'normal', paddingLeft: '3px' }}>[{datastreamsListComponent}]</span></div>;
                if (this.props.hideInputDatasetName) {

                    label = <div style={labelStyle}>{datastreamsListComponent}</div>;
                }
                datasetLabel = (
                    <div style={{ width: '100%', backgroundColor: AbstractFilteredList.COLOR_WHITE, paddingTop: '10px' }}>
                        {label}
                    </div>

                );
            }
        }
        const style = { backgroundColor: color }; // list item style

        // Style for no value.  We have to make the badge 1 px bigger because it appears
        // to be smaller on a white background
        const invisibleOutputDatastreamStyle = {
            backgroundColor: AbstractFilteredList.COLOR_WHITE,
            border: '1px solid #e8e8e8',
            width: '13px',
            height: '13px',
            marginLeft: '-4px'
        };

        const invisibleCoordinateSystemStyle = {
            backgroundColor: AbstractFilteredList.COLOR_WHITE,
            border: '1px solid #e8e8e8',
            width: '14px',
            height: '14px',
        };

        let badgedItemLabelStyle = null;
        let coordinateSystemBadge = <span />;
        let outputDatastreamBadge = <span />;

        if (!this.props.hideBadges) {
            // Badge style for coordinate system icon
            let coordinateSystemBadgeStyle = invisibleCoordinateSystemStyle;
            let coordinateSystemBadgeTitle = 'No transformation';
            if (value.coordinate_system) {
                coordinateSystemBadgeStyle = this.props.getBackgroundColor(Constants.TYPE_COORDINATE_SYSTEM, value.coordinate_system);
                coordinateSystemBadgeTitle = `Using ${value.coordinate_system} coordinate system`;
            }
            coordinateSystemBadge = <span className="badged-item circle" style={coordinateSystemBadgeStyle} title={coordinateSystemBadgeTitle}/>;

            // badge style for output datastream icons
            const outputDatastreams = ProcessUtils.getRetrievedVariableOutputDatastreams(this.props.process, name);
            let outputDatastreamBadges = outputDatastreams.map((outputDatastream, index) => {
                const outputDatastreamBadgeStyle = this.props.getBackgroundColor(Constants.TYPE_OUTPUT_DATASTREAM, outputDatastream);
                outputDatastreamBadgeStyle.marginLeft = '-5px';
                return <span key={outputDatastream} className="badged-item" style={outputDatastreamBadgeStyle} title={`Mapped to ${outputDatastream} datastream`}/>;
            });
            if (outputDatastreamBadges.length === 0) {
                outputDatastreamBadges = <span className="badged-item" style={invisibleOutputDatastreamStyle} title={'Not mapped to datastream'}/>;
            }
            outputDatastreamBadge = (
                <span className="badged-item-group">
                    {outputDatastreamBadges}
                </span>
            );

        } else if (this.props.showDataSources && !this.props.hideBadges) {
            //if badges are hidden and we're showing datasources we still want them to be indented
            badgedItemLabelStyle = css`padding-left:15px`;
        }

        const required = !!this.props.process.variable_retrieval.retrieved_variables[name].variable_required; //convert to int to bool

        const rowTitle = this.props.onDoubleClick ? 'Double click to edit.' : null;
        let variableTitle = this.props.onDoubleClick ? 'Double click to edit.' : '';
        variableTitle = required ? `Bold is required variable. ${variableTitle}` : variableTitle;

        let childString = dimensions;
        if (this.props.showUnits) {
            const units = ProcessUtils.getRetrievedVariableUnits(this.props.process, name);
            childString = `${childString}: ${units}`;
        }

        return (
            <div key={name}>
                {datasetLabel}
                <ListItem
                    name={name}
                    style={style}
                    className={this.getItemClassName()}
                    selected={selected}
                    onClick={this.onClick}
                    onDoubleClick={this.props.onDoubleClick}
                >
                    <Flexbox flexDirection="row" alignItems="center" title={rowTitle}>
                        {coordinateSystemBadge}
                        {outputDatastreamBadge}
                        <span style={{ whiteSpace: 'nowrap', flexGrow: 1 }}>
                            <span className={`badged-item-label ${cx(badgedItemLabelStyle)}`} title={variableTitle}>
                                <RollupError
                                    fieldPath={[...this.props.baseFieldPath, name]}
                                    processErrors={this.props.processErrors}
                                    process={this.props.process}
                                    includeChildren
                                />
                                {this.renderEntity(name, childString, required)}
                            </span>
                        </span>
                    </Flexbox>
                </ListItem>
            </div>
        );
    }
}



// WEBPACK FOOTER //
// ./src/components/page/process/variableRetrieval/RetrievedVariablesList.js